import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  MessageSquare,
  Trash2,
  Filter,
  Calendar,
} from 'lucide-react';
import {
  pushNotificationService,
  type AllNotificationsFilter,
  type PushNotification,
} from '@/services/push-notification.service';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AdminAllNotifications = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterViewed, setFilterViewed] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Build filter object
  const filters: AllNotificationsFilter = {
    page,
    limit,
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(filterType && { type: filterType }),
    ...(filterViewed !== '' && { viewed: filterViewed === 'true' }),
  };

  // Fetch all notifications
  const { data: notificationsData, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-all-notifications', filters],
    queryFn: async () => {
      return await pushNotificationService.getAllNotifications(filters);
    },
  });

  const notifications = notificationsData?.data?.notifications || [];
  const pagination = notificationsData?.data?.pagination;
  const uniqueTypes = useMemo(() => {
    const types = new Set<string>();
    notifications.forEach(n => {
      if (n.type) types.add(n.type);
    });
    return Array.from(types).sort();
  }, [notifications]);

  // Filter by search term
  const filteredNotifications = useMemo(() => {
    if (!searchTerm.trim()) return notifications;

    const term = searchTerm.toLowerCase();
    return notifications.filter(
      n =>
        n.title.toLowerCase().includes(term) ||
        n.message.toLowerCase().includes(term) ||
        n.email.toLowerCase().includes(term),
    );
  }, [notifications, searchTerm]);

  // Mark as viewed mutation
  const markAsViewedMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      return await pushNotificationService.markAsViewed(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-all-notifications'] });
      toast({
        title: 'Sucesso',
        description: 'Notificação marcada como visualizada',
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao atualizar notificação';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      return await pushNotificationService.deleteNotification(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-all-notifications'] });
      toast({
        title: 'Sucesso',
        description: 'Notificação deletada com sucesso',
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao deletar notificação';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleResetFilters = () => {
    setPage(1);
    setSearchTerm('');
    setFilterType('');
    setFilterViewed('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold">Todas as Notificações</h1>
          <p className="text-gray-600">Visualize e gerencie todas as notificações enviadas</p>
        </div>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros e Busca
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <Input
            placeholder="Buscar por título, mensagem ou email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full"
          />

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Data Inicial</label>
              <Input
                type="date"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Data Final</label>
              <Input
                type="date"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Tipo</label>
              <Select value={filterType} onValueChange={(val) => {
                setFilterType(val);
                setPage(1);
              }}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={filterViewed} onValueChange={(val) => {
                setFilterViewed(val);
                setPage(1);
              }}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Visualizados</SelectItem>
                  <SelectItem value="false">Não visualizados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reset Button */}
          {(searchTerm || filterType || filterViewed || startDate || endDate) && (
            <Button variant="outline" onClick={handleResetFilters} className="w-full">
              Limpar Filtros
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle>Notificações</CardTitle>
          <CardDescription>
            {pagination && (
              <>
                Mostrando {((page - 1) * limit) + 1} a {Math.min(page * limit, pagination.total)} de {pagination.total} notificações
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Carregando notificações...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Erro ao carregar notificações</p>
                <p className="text-sm">
                  {error instanceof Error ? error.message : 'Tente novamente'}
                </p>
              </div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">Nenhuma notificação encontrada</p>
              <p className="text-sm">Tente ajustar os filtros ou a busca</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    notification.viewed
                      ? 'border-gray-200 bg-white'
                      : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  {/* Header with status */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3 flex-1">
                      {notification.viewed ? (
                        <Eye className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{notification.title}</h3>
                        <p className="text-xs text-gray-600 truncate">{notification.email}</p>
                      </div>
                    </div>
                    {notification.type && (
                      <span className="inline-block px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium ml-2 flex-shrink-0">
                        {notification.type}
                      </span>
                    )}
                  </div>

                  {/* Message */}
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                    {notification.message}
                  </p>

                  {/* Path and dates */}
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-gray-600">
                    <span>
                      <Calendar className="h-3 w-3 inline mr-1" />
                      {format(parseISO(notification.createdAt.toString()), 'dd/MM/yyyy HH:mm', {
                        locale: ptBR,
                      })}
                    </span>
                    {notification.viewedAt && (
                      <span className="text-green-600">
                        Visualizado em{' '}
                        {format(parseISO(notification.viewedAt.toString()), 'dd/MM/yyyy HH:mm', {
                          locale: ptBR,
                        })}
                      </span>
                    )}
                    {notification.path && (
                      <span className="text-blue-600">Rota: {notification.path}</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {!notification.viewed && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => markAsViewedMutation.mutate(notification.id)}
                        disabled={markAsViewedMutation.isPending}
                        className="text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Marcar como visualizado
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteNotificationMutation.mutate(notification.id)}
                      disabled={deleteNotificationMutation.isPending}
                      className="text-xs"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Deletar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-600">
                Página {page} de {pagination.pages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}

          {/* Limit selector */}
          <div className="flex items-center justify-between pt-4 border-t">
            <label className="text-sm text-gray-600">Itens por página:</label>
            <Select value={limit.toString()} onValueChange={(val) => {
              setLimit(parseInt(val));
              setPage(1);
            }}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAllNotifications;
